import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useCallback, useMemo} from "react";
import {PhoneNumber, PhoneNumberDaten, PromptListe} from "../../api";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {useUpdatePhoneNumber, useSetActiveIcpFallbackPrompt, useDeactivateIcpFallbackPrompt} from "../../client/hooks";
import type {PhoneNumberFormValues} from "../../types/phonenumberTypes";

export function usePhoneNumberUpdateForm(phoneNumber: PhoneNumber, versions: PromptListe): FormikConfig<PhoneNumberFormValues> {
    const {t: translation} = useTranslation("phonenumbers");
    const {enqueueSnackbar} = useSnackbar();
    const {mutateAsync} = usePhoneNumberUpdate(phoneNumber.id);
    const setActivePrompt = useSetActiveIcpFallbackPrompt();
    const deactivatePrompt = useDeactivateIcpFallbackPrompt();
    const queryClient = useQueryClient();

    const wasInboundActive = !!phoneNumber.activeIcpFallbackPromptId;

    const initialValues: PhoneNumberFormValues = useMemo(() => ({
        descriptor: phoneNumber.descriptor ?? "",
        isGlobal: phoneNumber.isGlobal ?? false,
        sprache: phoneNumber.sprache ?? "de-DE",
        stimmeId: phoneNumber.stimmeId ?? "",
        speechModel: phoneNumber.speechModel ?? "",
        empathie: phoneNumber.empathie ?? 0,
        inboundAufgabeId: phoneNumber.inboundAufgabeId ?? "",
        inboundEnabled: wasInboundActive,
    }), [phoneNumber, wasInboundActive]);

    const initialTouched = useMemo(() => ({
        descriptor: true,
        isGlobal: true,
        sprache: true,
        stimmeId: true,
        speechModel: true,
        empathie: true,
        inboundAufgabeId: true,
        inboundEnabled: true,
    }), []);

    const invalidatePhoneNumberQueries = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries(["api", "v1", "phonenumbers", phoneNumber.id, "icp-fallback-prompts"]),
            queryClient.invalidateQueries(["api", "v1", "phonenumbers", phoneNumber.id]),
            queryClient.invalidateQueries(["api", "v1", "phonenumbers"]),
            queryClient.invalidateQueries(["api", "v1", "prompts"]),
        ]);
    }, [queryClient, phoneNumber.id]);

    const onSubmit = useCallback(async (
        values: PhoneNumberFormValues,
        {setSubmitting, setStatus}: FormikHelpers<PhoneNumberFormValues>,
    ): Promise<void> => {
        try {
            const data: PhoneNumberDaten = {
                number: phoneNumber.number,
                descriptor: values.descriptor.trim() || undefined,
                isGlobal: values.isGlobal,
                sprache: values.sprache.trim() || "de-DE",
                stimmeId: values.stimmeId?.trim() || undefined,
                speechModel: values.speechModel?.trim() || undefined,
                empathie: values.empathie,
                inbound_config_duration_in_hours: null,
                inboundAufgabeId: values.inboundAufgabeId?.trim() || null,
            };
            await mutateAsync(data);

            // Handle inbound activation/deactivation
            if (values.inboundEnabled && !wasInboundActive) {
                // Activating: set the first available prompt as active
                if (versions.items.length > 0) {
                    await setActivePrompt(phoneNumber.id, versions.items[0].id);
                }
            } else if (!values.inboundEnabled && wasInboundActive) {
                // Deactivating
                await deactivatePrompt(phoneNumber.id, '_unused');
            }

            await invalidatePhoneNumberQueries();
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation("phonenumber-update-successful")), {variant: "success"});
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation("phonenumber-update-failed")), {variant: "error"});
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation, phoneNumber.number, phoneNumber.id, wasInboundActive, versions.items, setActivePrompt, deactivatePrompt, invalidatePhoneNumberQueries]);

    return useMemo(() => ({
        enableReinitialize: true,
        initialTouched,
        initialValues,
        onSubmit,
    }), [initialValues, onSubmit, initialTouched]);
}

function usePhoneNumberUpdate(phoneNumberId: string) {
    const updatePhoneNumber = useUpdatePhoneNumber();
    const queryClient = useQueryClient();

    return useMutation(
        (data: PhoneNumberDaten): Promise<PhoneNumber> => updatePhoneNumber(phoneNumberId, data),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["api", "v1", "phonenumbers"]);
                await queryClient.invalidateQueries(["api", "v1", "phonenumbers", phoneNumberId]);
            },
        },
    );
}
